import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileWarning, RefreshCw } from "lucide-react";
import { API, th, td, StatPill, Empty } from "./shared";

const ctl = { border: "1px solid #CBD5E1", borderRadius: 7, padding: "6px 8px", fontSize: 12, fontFamily: "inherit", minWidth: 0, boxSizing: "border-box" };
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

function QueueRow({ r, zoho, onDone }) {
  const d = r.details || {};
  const [km, setKm] = useState(d.distance_km || "");
  const [num, setNum] = useState("");
  const [busy, setBusy] = useState("");
  const run = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "zoho") {
        const { data } = await axios.post(`${API}/admin/jobs/${r.job_id}/ewaybill/zoho`, {
          from_pincode: d.from_pincode, distance_km: Number(km), vehicle_no: d.vehicle_no || null, transporter_id: d.transporter_id || null });
        toast.success(`E-way bill ${data.number} generated`);
      } else {
        await axios.post(`${API}/admin/jobs/${r.job_id}/ewaybill`, { number: num });
        toast.success("E-way bill number saved");
      }
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(""); }
  };
  return (
    <tr data-testid={`ewbq-row-${r.job_id}`} style={{ borderBottom: "1px solid #e5e7eb", verticalAlign: "top" }}>
      <td style={td}><b>{r.title}</b><div style={{ fontSize: 10.5, color: "#9ca3af" }}>GPP-{r.job_id}</div></td>
      <td style={td}>{r.supplier_name || "—"}<div style={{ fontSize: 10.5, color: "#9ca3af" }}>{r.supplier_email}</div></td>
      <td style={td}>{r.from_state || "?"} → {r.to_state || "?"}<div style={{ fontSize: 10.5, color: "#9ca3af" }}>{r.to_city} {r.to_pincode}</div></td>
      <td style={{ ...td, textAlign: "right", fontWeight: 600 }}>{inr(r.value_inr)}<div style={{ fontSize: 10.5, color: "#9ca3af", fontWeight: 400 }}>limit {inr(r.limit_inr)}</div></td>
      <td style={td} data-testid={`ewbq-details-${r.job_id}`}>
        {r.details ? <>Pickup {d.from_pincode}{d.vehicle_no && <div>Vehicle {d.vehicle_no}</div>}{d.transporter_id && <div>Transporter {d.transporter_id}</div>}
          <div style={{ fontSize: 10.5, color: "#9ca3af" }}>sent {String(d.submitted_at).slice(0, 10)}</div></>
          : <span style={{ color: "#B45309", fontWeight: 600 }}>Waiting for printer</span>}
        {r.auto?.status === "failed" && <div data-testid={`ewbq-auto-error-${r.job_id}`} style={{ fontSize: 10.5, color: "#B91C1C", marginTop: 4, maxWidth: 220 }}>Auto: {r.auto.error}</div>}
      </td>
      <td style={td}>
        <div style={{ display: "grid", gap: 6, minWidth: 230 }}>
          {zoho && r.details && (
            <div style={{ display: "flex", gap: 6 }}>
              <input data-testid={`ewbq-km-${r.job_id}`} type="number" min="1" placeholder="km" value={km} onChange={e => setKm(e.target.value)} style={{ ...ctl, width: 70 }}/>
              <button data-testid={`ewbq-zoho-${r.job_id}`} className="primary-btn" style={{ fontSize: 11.5, padding: "5px 12px" }} disabled={!!busy || !km} onClick={() => run("zoho")}>
                {busy === "zoho" ? "Generating…" : "Generate via Zoho"}
              </button>
            </div>
          )}
          <div style={{ display: "flex", gap: 6 }}>
            <input data-testid={`ewbq-num-${r.job_id}`} placeholder="12-digit EWB no." value={num} onChange={e => setNum(e.target.value.replace(/[^\d\s]/g, ""))} style={{ ...ctl, width: 140 }}/>
            <button data-testid={`ewbq-save-${r.job_id}`} className="secondary-btn" style={{ fontSize: 11.5, padding: "5px 10px" }} disabled={!!busy || num.replace(/\s/g, "").length !== 12} onClick={() => run("manual")}>Save</button>
          </div>
        </div>
      </td>
    </tr>
  );
}

export function EwayBillQueue() {
  const [data, setData] = useState(null);
  const load = () => axios.get(`${API}/admin/ewaybill/queue`).then(r => setData(r.data)).catch(() => setData({ rows: [] }));
  useEffect(() => { load(); }, []);
  return (
    <section className="content" data-testid="ewb-queue-page">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Compliance</p>
          <h2>E-way bill queue</h2>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#64748b" }}>Awarded goods orders above the e-way bill limit that can't be dispatched yet. Generate on the Tejas Industries invoice (Bill From: Tejas Industries · Dispatch From: printer's pickup pincode · Ship To: buyer).</p>
        </div>
        <button data-testid="ewbq-refresh" className="secondary-btn" onClick={load}><RefreshCw size={14}/> Refresh</button>
      </div>
      {!data ? <div style={{ fontSize: 13, color: "#64748b" }}>Loading…</div> : (
        <>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", margin: "0 0 14px" }}>
            <StatPill label="Waiting" value={data.count || 0} tone="warn" testid="ewbq-count"/>
            <StatPill label="Ready to generate" value={data.ready_count || 0} tone="ok" testid="ewbq-ready"/>
            <StatPill label="Zoho" value={data.zoho_configured ? "Connected" : "Not connected"} testid="ewbq-zoho"/>
          </div>
          {data.rows.length === 0 ? <Empty text="No orders are waiting for an e-way bill."/> : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", fontSize: 12.5, borderCollapse: "collapse" }}>
                <thead><tr style={{ background: "#f3f4f6", textAlign: "left" }}>
                  <th style={th}>Order</th><th style={th}>Printer</th><th style={th}>Route</th><th style={{ ...th, textAlign: "right" }}>Value (incl. GST)</th>
                  <th style={th}>Printer's details</th><th style={th}><FileWarning size={13} style={{ verticalAlign: "-2px" }}/> Generate / add</th>
                </tr></thead>
                <tbody>{data.rows.map(r => <QueueRow key={r.job_id} r={r} zoho={data.zoho_configured} onDone={load}/>)}</tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}
